import React, {Component, ReactNode} from "react"

import id from "@irati/utils/id"
import sentry from "@irati/utils/services/sentry"
import {getErrorMessage} from "@irati/utils/lang/formatError"
import {deriveLoad, ApolloLoad} from "@irati/utils/services/apollo/helpers/hookShared"
import {hangPromise} from "@irati/utils/promise"

import DefaultLoader from "./Loader"
import DefaultError from "./Error"

export type LoadType = Promise<any> | null | undefined | ApolloLoad | ApolloLoad[]

type InternalLoadType = Promise<any> | null | undefined

interface Props {
  load: LoadType
  Error?: typeof DefaultError
  Loader?: typeof DefaultLoader | ((props: Props) => JSX.Element)
  useLoader?: boolean
  isLoading?: boolean
  initLoad?: boolean
  /** if true, renders children without a wrapping `div` */
  inline?: boolean
  children?: ReactNode
}

interface State {
  loaded?: boolean
  errorMsg?: string | null
  error?: PBError
}

export interface ErrorProps {
  error: PBError
  errorMsg: string
}

export default class Loading extends Component<Props, State> {
  static defaultProps: Props = {
    load: null,
    Error: DefaultError,
    Loader: DefaultLoader,
    useLoader: true,
    initLoad: false
  }

  __isMounted: boolean

  constructor(props: Props) {
    super(props)
    this.load = this.load.bind(this)

    const loadProp = deriveLoad(props.load)
    const load = !loadProp && props.initLoad ? hangPromise() : loadProp
    const loaded = this.load(load)

    this.state = {loaded, errorMsg: null}

    this.__isMounted = true
  }

  componentWillUnmount() {
    this.__isMounted = false
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const nextLoadProp = deriveLoad(nextProps.load)
    const loadProp = deriveLoad(this.props.load)
    if (nextLoadProp !== loadProp) {
      const loaded = this.load(nextLoadProp)
      this.setState({loaded, errorMsg: null})
    }
  }

  isLoaded(promise: any): boolean {
    const isPromise = promise && "then" in promise
    // other checks?
    return isPromise
  }

  load(load: InternalLoadType) {
    let loaded = true
    if (load && this.isLoaded(load)) {
      loaded = false
      load
        .then(() => {
          // only update state if this is the current promise in props, or an ApolloLoad type
          const isCurrent = load === deriveLoad(this.props.load)
          if (isCurrent && this.__isMounted) {
            this.setState({loaded: true, errorMsg: null})
          }
        })
        .catch((error) => {
          /** Helper for devs to find the root issue easier. Throw is sentry too. */
          console.error(error)
          if (error && error.name === "TypeError") {
            console.error(error)
            sentry.captureException(error)
          }
          if (this.__isMounted) {
            this.setState({loaded: false, errorMsg: getErrorMessage(error), error})
          }
        })
    }

    return loaded
  }

  render() {
    const {loaded, errorMsg, error} = this.state
    const {children, Error, useLoader, Loader, isLoading} = this.props
    const idNamespace = id("loading", this.props)

    if (errorMsg) {
      return (
        <Error id={id("copy-error", idNamespace)} className="perkboxLoading_error" errorMsg={errorMsg} error={error}>
          {children}
        </Error>
      )
    }

    if ((!loaded && useLoader) || isLoading) {
      return <Loader id={id("loader", idNamespace)}>{children}</Loader>
    }

    return this.props.inline ? <>{children}</> : <div id={idNamespace}>{children}</div>
  }
}
